import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, Clipboard, KeyRound, Pencil, Plus, Users } from "lucide-react";
import { toast } from "sonner";
import Modal from "../components/Modal";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  DataValue,
  PassiveBadge,
  formatPresentationDate,
} from "../components/ui/data-presentation";
import {
  ListTable,
  ListTableShell,
  ListTableState,
  ListTableViewport,
} from "../components/ui/list-table";
import {
  ClearAllFilters,
  ColumnDateFilter,
  ColumnFilterCell,
  ColumnFilterRow,
  ColumnSelectFilter,
  ColumnTextFilter,
  useSearchFilterState,
} from "../components/ui/search-filter";
import { OrdinaryPageBody, OrdinaryPageHeader } from "../components/ui/page-structure";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import {
  createUser,
  listUsers,
  resetStaffPassword,
  updateUser,
} from "../lib/userApi";
import useUnsavedChangesGuard from "../hooks/useUnsavedChangesGuard";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const COPY_FEEDBACK_MS = 2000;
const ROLE_BADGE_TONES = {
  admin: "category",
  staff: "info",
};
const MANAGE_USERS_TABLE_GEOMETRY = Object.freeze([
  { key: "name", kind: "fixed", width: 220 },
  { key: "email", kind: "fill", minWidth: 300 },
  { key: "role", kind: "fixed", width: 120 },
  { key: "created-at", kind: "fixed", width: 150 },
  { key: "actions", kind: "utility", width: 104 },
]);

function validateUserForm(name, email, record) {
  const normalizedName = name.trim();
  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedName) return { error: "Name is required." };
  if (normalizedName.length > 100) return { error: "Name must be at most 100 characters." };
  if (!normalizedEmail) return { error: "Email is required." };
  if (normalizedEmail.length > 254) return { error: "Email must be at most 254 characters." };
  if (!EMAIL_PATTERN.test(normalizedEmail)) return { error: "Enter a valid email address." };
  if (
    record &&
    normalizedName === record.name &&
    normalizedEmail === String(record.email || "").toLowerCase()
  ) {
    return { error: "Make a change before saving." };
  }
  return { payload: { name: normalizedName, email: normalizedEmail } };
}

function normalizeUserDraft(name, email) {
  return {
    name: String(name || "").trim(),
    email: String(email || "").trim().toLowerCase(),
  };
}

function UserFormModal({ record, onClose, onSaved }) {
  const [name, setName] = useState(record?.name || "");
  const [email, setEmail] = useState(record?.email || "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const baselineRef = useRef(normalizeUserDraft(record?.name, record?.email));

  useEffect(() => {
    baselineRef.current = normalizeUserDraft(record?.name, record?.email);
  }, [record]);

  const isDirty = JSON.stringify(normalizeUserDraft(name, email)) !== JSON.stringify(baselineRef.current);
  const leaveGuard = useUnsavedChangesGuard(isDirty && !busy);
  const requestClose = () => leaveGuard.requestLeave(onClose);

  async function submit(event) {
    event.preventDefault();
    if (busy) return;
    const validation = validateUserForm(name, email, record);
    if (validation.error) {
      setError(validation.error);
      return;
    }

    setBusy(true);
    setError("");
    try {
      const response = record
        ? await updateUser(record.id, validation.payload)
        : await createUser(validation.payload);
      baselineRef.current = normalizeUserDraft(validation.payload.name, validation.payload.email);
      await onSaved(response, record || null);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  const role = record?.role || "staff";
  return (
    <Modal
      title={record ? "Edit User" : "Create Staff User"}
      onClose={requestClose}
      busy={busy}
      size="sm"
      testid="manage-users-form-modal"
    >
      <form onSubmit={submit} className="space-y-5" data-testid="manage-users-form">
        <div>
          <label htmlFor="manage-user-name" className="text-xs font-medium text-zinc-700">
            Name <span className="text-destructive">*</span>
          </label>
          <Input
            id="manage-user-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="mt-1.5"
            autoFocus
            autoComplete="name"
            data-testid="manage-user-name"
          />
        </div>
        <div>
          <label htmlFor="manage-user-email" className="text-xs font-medium text-zinc-700">
            Email <span className="text-destructive">*</span>
          </label>
          <Input
            id="manage-user-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="mt-1.5"
            autoComplete="email"
            data-testid="manage-user-email"
          />
        </div>
        <div>
          <div className="text-xs font-medium text-zinc-700">Role</div>
          <div
            className="field-readonly mt-1.5 flex h-10 items-center rounded-sm border px-3 text-sm capitalize"
            data-testid="manage-user-role-readonly"
          >
            {role}
          </div>
          {!record ? (
            <p className="mt-1.5 text-xs text-zinc-500">New accounts are created as Staff.</p>
          ) : null}
        </div>
        {error ? (
          <p className="text-sm text-destructive" role="alert" data-testid="manage-users-form-error">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2 border-t border-zinc-200 pt-4">
          <Button type="button" variant="cancel" onClick={requestClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="success" loading={busy} loadingLabel="Saving..." data-testid="manage-users-save">
            {record ? "Save Changes" : "Create User"}
          </Button>
        </div>
      </form>
      {leaveGuard.dialog}
    </Modal>
  );
}

function PasswordResultModal({ result, onClose }) {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState("");
  const copiedTimerRef = useRef(null);

  useEffect(() => () => {
    if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
  }, []);

  async function copyPassword() {
    setCopyError("");
    try {
      await navigator.clipboard.writeText(result.generatedPassword);
      setCopied(true);
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = window.setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
    } catch {
      setCopied(false);
      setCopyError("Could not copy the password. Copy it manually before closing this window.");
    }
  }

  return (
    <Modal
      title="One-Time Password"
      onClose={onClose}
      size="sm"
      testid="manage-users-result-modal"
    >
      <div className="space-y-5">
        <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-zinc-900">
            <KeyRound className="h-4 w-4 text-primary" />
            Password for {result.user.name}
          </div>
          <div
            className="mt-3 break-all rounded-sm border border-zinc-300 bg-card px-3 py-3 text-sm font-semibold tracking-wide"
            data-testid="manage-users-generated-password"
          >
            {result.generatedPassword}
          </div>
        </div>
        <p className="text-sm text-zinc-600">
          This password is shown only once. Copy it before closing this window.
        </p>
        {copyError ? (
          <p className="text-sm text-destructive" role="alert" data-testid="manage-users-copy-error">
            {copyError}
          </p>
        ) : null}
        <div className="flex justify-end gap-2 border-t border-zinc-200 pt-4">
          <Button type="button" variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button
            type="button"
            variant="neutral"
            onClick={copyPassword}
            data-testid="manage-users-copy-password"
          >
            {copied ? <Check className="h-4 w-4" /> : <Clipboard className="h-4 w-4" />}
            {copied ? "Copied" : "Copy Password"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ResetPasswordModal({ target, onClose, onReset }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function confirmReset() {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const response = await resetStaffPassword(target.id);
      await onReset(response);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Reset Staff Password"
      onClose={onClose}
      busy={busy}
      size="sm"
      testid="manage-users-reset-modal"
    >
      <div className="space-y-5">
        <p className="text-sm text-zinc-700">
          Generate a new password for <span className="font-semibold">{target.name}</span>?
          Their previous password will stop working.
        </p>
        {error ? (
          <p className="text-sm text-destructive" role="alert" data-testid="manage-users-reset-error">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2 border-t border-zinc-200 pt-4">
          <Button type="button" variant="cancel" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="warning"
            onClick={confirmReset}
            loading={busy}
            loadingLabel="Resetting..."
            data-testid="manage-users-confirm-reset"
          >
            Confirm Reset
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function formatCreatedAt(value) {
  return formatPresentationDate(value);
}

export default function ManageUsers() {
  const { user, refreshMe } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(undefined);
  const [resetTarget, setResetTarget] = useState(null);
  const [passwordResult, setPasswordResult] = useState(null);
  const [filterDrafts, setFilterDrafts] = useState({
    name: "",
    email: "",
    role: "",
    createdAt: "",
  });
  const searchFilters = useSearchFilterState();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await listUsers();
      setUsers(Array.isArray(response) ? response : []);
    } catch (requestError) {
      setUsers([]);
      setError(formatApiError(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function formSaved(response, editedRecord) {
    setEditing(undefined);
    if (editedRecord?.id === user?.id) await refreshMe();
    await load();
    if (editedRecord) {
      toast.success("User updated successfully.");
    } else {
      setPasswordResult({
        user: response.user,
        generatedPassword: response.generated_password,
      });
    }
  }

  async function passwordReset(response) {
    setResetTarget(null);
    await load();
    setPasswordResult({
      user: response.user,
      generatedPassword: response.generated_password,
    });
  }

  function closePasswordResult() {
    setPasswordResult(null);
  }

  const appliedFilters = searchFilters.state.columnFilters;
  const filteredUsers = useMemo(() => users.filter((record) => {
    if (appliedFilters.name && !String(record.name || "").toLowerCase().includes(appliedFilters.name.toLowerCase())) return false;
    if (appliedFilters.email && !String(record.email || "").toLowerCase().includes(appliedFilters.email.toLowerCase())) return false;
    if (appliedFilters.role && record.role !== appliedFilters.role) return false;
    if (appliedFilters.createdAt && String(record.created_at || "").slice(0, 10) !== appliedFilters.createdAt) return false;
    return true;
  }), [appliedFilters, users]);
  const hasAppliedFilters = Object.values(appliedFilters).some(Boolean);

  function updateFilterDraft(name, value) {
    setFilterDrafts((current) => ({ ...current, [name]: value }));
  }

  function clearAllFilters() {
    setFilterDrafts({ name: "", email: "", role: "", createdAt: "" });
    searchFilters.clearAll();
  }

  return (
    <OrdinaryPageBody data-testid="manage-users-page">
      <OrdinaryPageHeader
        title="Manage Users"
        actions={<Button onClick={() => setEditing(null)} data-testid="manage-users-create">
          <Plus className="h-4 w-4" /> Create Staff User
        </Button>}
        data-testid="manage-users-page-header"
      />

      <ListTableShell>
        <ListTableViewport>
          <ListTable geometry={MANAGE_USERS_TABLE_GEOMETRY} data-testid="manage-users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Created At</th>
              <th className="text-right">Actions</th>
            </tr>
            <ColumnFilterRow className="bg-white">
              <ColumnFilterCell><ColumnTextFilter value={filterDrafts.name} onValueChange={(value) => updateFilterDraft("name", value)} onApply={(value) => searchFilters.applyColumnFilter("name", value)} placeholder="Filter…" aria-label="Filter user name" data-testid="manage-users-filter-name" /></ColumnFilterCell>
              <ColumnFilterCell><ColumnTextFilter value={filterDrafts.email} onValueChange={(value) => updateFilterDraft("email", value)} onApply={(value) => searchFilters.applyColumnFilter("email", value)} placeholder="Filter…" aria-label="Filter user email" data-testid="manage-users-filter-email" /></ColumnFilterCell>
              <ColumnFilterCell><ColumnSelectFilter value={filterDrafts.role} onValueChange={(value) => updateFilterDraft("role", value)} onApply={(value) => searchFilters.applyColumnFilter("role", value)} placeholder="All" options={[{ value: "admin", label: "Admin" }, { value: "staff", label: "Staff" }]} aria-label="Filter user role" data-testid="manage-users-filter-role" /></ColumnFilterCell>
              <ColumnFilterCell><ColumnDateFilter value={filterDrafts.createdAt} onValueChange={(value) => updateFilterDraft("createdAt", value)} onApply={(value) => searchFilters.applyColumnFilter("createdAt", value)} aria-label="Filter user created date" data-testid="manage-users-filter-created-at" /></ColumnFilterCell>
              <ColumnFilterCell className="align-middle" aria-label="Actions filter controls">
                <div className="flex h-7 w-full items-center justify-end">
                  {hasAppliedFilters ? <ClearAllFilters onClick={clearAllFilters} data-testid="manage-users-filter-clear-all" /> : null}
                </div>
              </ColumnFilterCell>
            </ColumnFilterRow>
          </thead>
          <tbody>
            {loading ? (
              <ListTableState colSpan={5} kind="loading">Loading users...</ListTableState>
            ) : error ? (
              <ListTableState colSpan={5} kind="error" onRetry={load}>{error}</ListTableState>
            ) : null}
            {!loading && !error && filteredUsers.length === 0 ? (
              <ListTableState colSpan={5} kind={hasAppliedFilters ? "filtered-empty" : "empty"}>
                {hasAppliedFilters ? "No users match the current filters." : "No users found."}
              </ListTableState>
            ) : null}
            {!loading && !error ? filteredUsers.map((record) => (
              <tr key={record.id} data-testid={`manage-users-row-${record.id}`}>
                <td className="max-w-[240px] font-medium text-zinc-900"><DataValue value={record.name} /></td>
                <td className="max-w-[280px] text-zinc-600"><DataValue value={record.email} containment="break-all" /></td>
                <td>
                  <PassiveBadge
                    tone={ROLE_BADGE_TONES[record.role] || "neutral"}
                    data-testid={`manage-users-role-${record.id}`}
                  >
                    {record.role === "admin" ? "Admin" : "Staff"}
                  </PassiveBadge>
                </td>
                <td className="text-zinc-600">{formatCreatedAt(record.created_at)}</td>
                <td>
                  <div className="flex justify-end gap-2">
                    {record.role === "staff" ? (
                      <Button
                        type="button"
                        variant="warning"
                        size="row-icon"
                        title="Reset password"
                        aria-label={`Reset password for ${record.email}`}
                        onClick={() => setResetTarget(record)}
                        data-testid={`manage-users-reset-${record.id}`}
                      >
                        <KeyRound />
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      variant="edit"
                      size="row-icon"
                      title="Edit user"
                      aria-label={`Edit user ${record.email}`}
                      onClick={() => setEditing(record)}
                      data-testid={`manage-users-edit-${record.id}`}
                    >
                      <Pencil />
                    </Button>
                  </div>
                </td>
              </tr>
            )) : null}
          </tbody>
          </ListTable>
        </ListTableViewport>
      </ListTableShell>

      {editing !== undefined ? (
        <UserFormModal
          record={editing}
          onClose={() => setEditing(undefined)}
          onSaved={formSaved}
        />
      ) : null}
      {resetTarget ? (
        <ResetPasswordModal
          target={resetTarget}
          onClose={() => setResetTarget(null)}
          onReset={passwordReset}
        />
      ) : null}
      {passwordResult ? (
        <PasswordResultModal result={passwordResult} onClose={closePasswordResult} />
      ) : null}
    </OrdinaryPageBody>
  );
}
